import { Navigate } from "react-router";
import { useExperience } from "@shared-core/domain/experience";
import { HOME_PERSONAS, useHomePersona } from "@shared-core/domain/homePersona";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import { useAuth } from "../auth/hooks";
import { ONE_LANDING_OBJECT, areaObjectIds } from "./domain/one/one-nav";

/**
 * Index-route component: redirects ``/`` to the current persona's home
 * (`/home/sales` or `/home/ops`). Resolves persona from the per-device
 * localStorage preference, falling back to a role-based default.
 *
 * In One, ``/`` opens the Orders list (Ben, 13 Oct), or the first object the
 * modules declare while Orders is not declared; with nothing declared yet it
 * falls back to Classic's home.
 *
 * Replaces the previous behaviour of rendering ``DashboardHome`` inline
 * at ``/`` — both dashboards now have explicit URLs and the index is a
 * redirect, so a manual page reload keeps the user on the same view.
 */
export function HomeRedirect() {
  const { experience } = useExperience();
  const index = useObjectManifest();
  if (experience === "one") {
    const landing = [
      ONE_LANDING_OBJECT,
      ...areaObjectIds(index.manifest.areas),
    ].find((id) => index.isObjectEnabled(id) && index.objectOf(id).list);
    if (landing)
      return <Navigate to={paths.objectList({ objectId: landing })} replace />;
  }
  return <ClassicHomeRedirect />;
}

function ClassicHomeRedirect() {
  const { user } = useAuth();
  const { persona } = useHomePersona({ role: user?.role });
  // The persona is a per-device preference that outlives any one environment,
  // so a browser carrying `ops` from staging would otherwise land on the
  // not-found page at `/` in an environment without `production_ops` — the one
  // URL that must always resolve to something. Sales home is the fallback
  // because `sales_home` is the feature that owns `/` itself.
  const home = HOME_PERSONAS.find((p) => p.persona === persona);
  const target =
    home && isRouteEnabled(home.route) ? home.path : paths.homeSales();
  return <Navigate to={target} replace />;
}
